import type {PartialOptions} from "../../../stem-core/ui/UIBase";
import {type UIChild, UI} from "../../../stem-core/ui/UIBase";
import type {NodeAttributes} from "../../../stem-core/ui/NodeAttributes";
import {StyleSheet} from "../../../stem-core/ui/Style";
import {styleRule} from "../../../stem-core/decorators/Style";
import {Device} from "../../../stem-core/base/Device";
import {registerStyle} from "../../../stem-core/ui/style/Theme";
import {Image} from "../../../stem-core/ui/primitives/Image.jsx";
import {RawHTML} from "../../../stem-core/ui/RawHTML.jsx";
import {isDesktopSmall} from "../../Utils";
import {IphoneFrame} from "../../SVGElements";
import type {LandingPageMedia} from "../../sections-publisher/product-pages/PayPage";

class WalletTextImageSectionStyle extends StyleSheet {
    @styleRule
    section = {
        display: "flex",
        paddingBottom: () => (isDesktopSmall() ? 0 : 90),
        flexDirection: () => (isDesktopSmall() ? "column-reverse" : ""),
        alignItems: "center",
    };

    @styleRule
    imageContainer = {
        flex: 0.93,
        textAlign: "center",
        position: "relative",
    };

    media = {
        maxWidth: "100%",
        maxHeight: 600,
    };

    @styleRule
    image = {
        ...this.media,
    };

    @styleRule
    video = {
        ...this.media,
        width: () => (Device.isMobileDevice() ? "calc(100% - 7%)" : "100%"),
        bottom: () => (Device.isMobileDevice() ? "2%" : 14),
        borderRadius: "0 0 7% 7%",
        position: "absolute",
        zIndex: -1,
        transform: "translateX(-50%)",
    };

    @styleRule
    textContainer = {
        flex: 1,
        paddingTop: () => (Device.isMobileDevice() ? 36 : 20),
    };

    @styleRule
    title = {
        fontSize: () => (Device.isMobileDevice() ? 24 : 36),
        marginBottom: () => (Device.isMobileDevice() ? 24 : 36),
        textAlign: () => (Device.isMobileDevice() ? "center" : ""),
    };

    @styleRule
    paragraph = {
        fontSize: () => (Device.isMobileDevice() ? 16 : 21),
        lineHeight: () => (Device.isMobileDevice() ? 24 : 33),
        marginBottom: () => (Device.isMobileDevice() ? 24 : 33),
        fontWeight: "lighter",
    };

    @styleRule
    leftContent = {
        paddingLeft: () => (isDesktopSmall() ? "" : 40),
    };

    @styleRule
    iphoneVideoContainer = {
        margin: "auto",
        width: 364,
        maxWidth: "100%",
        position: "relative",
    }
}

interface WalletTextImageSectionOptions {
    mediaUrl?: string | LandingPageMedia[];
    description?: UIChild[];
    imagePositionLeft?: boolean;
    isVideo?: boolean;
    hasShadow?: boolean;
}

@registerStyle(WalletTextImageSectionStyle)
export class WalletTextImageSection extends UI.Element<WalletTextImageSectionOptions> {
    getDefaultOptions(): PartialOptions<WalletTextImageSection> {
        return {
            ...super.getDefaultOptions(),
            mediaUrl: null,
            title: null,
            description: null,
            imagePositionLeft: false,
            isVideo: false,
            hasShadow: false,
        };
    }

    extraNodeAttributes(attr: NodeAttributes) {
        super.extraNodeAttributes(attr);

        attr.addClass(this.styleSheet.section);
        if (this.options.imagePositionLeft && !isDesktopSmall()) {
            attr.setStyle({flexDirection: "row-reverse"});
        }
    }

    getVideo() {
        const {mediaUrl} = this.options;
        // Only reached when isVideo, and then mediaUrl is always the source list
        const mediaUrls = (Array.isArray(this.options.mediaUrl) ? mediaUrl : [mediaUrl]) as LandingPageMedia[];
        const sourceHtml = mediaUrls.map(({url, type}) => `<source src="${url}" type="${type}">`).join("");

        return (
            <RawHTML
                innerHTML={`<video autoplay="" loop="" muted="" playsinline="" data-object-fit="cover"
                class="${this.styleSheet.video}">${sourceHtml}</video>`}
            />
        );
    }

    render() {
        const {styleSheet} = this;
        const {mediaUrl, title, description, imagePositionLeft, isVideo} = this.options;

        return [
            <div className={styleSheet.imageContainer + (imagePositionLeft ? styleSheet.leftContent : "")}>
                {isVideo ? (
                    <div className={styleSheet.iphoneVideoContainer}>
                        <IphoneFrame />
                        {this.getVideo()}
                    </div>
                ) : (
                    <Image src={mediaUrl as string} className={styleSheet.image} />
                )}
            </div>,
            <div className={styleSheet.textContainer + (imagePositionLeft ? "" : styleSheet.leftContent)}>
                <div className={styleSheet.title}>{title}</div>
                {description.map(paragraph => (
                    <div className={styleSheet.paragraph}>{paragraph}</div>
                ))}
            </div>,
        ];
    }
}
